import { Component, Input, OnChanges } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators';
import { defaultImagePlaceholder } from '../image';
import { MiqSecuredResourcesService } from '../miq-secure-resources/miq-secured-resources.service';
import { ImageContainerBGColor, SecureImage } from './bg-image.models';

@Component({
  // eslint-disable-next-line
  selector: '[miq-bg-image]',
  template: `
    <div class="image-icons">
      <mat-icon
        *ngIf="showImage && !publishable && !isPDF"
        class="no-publish-icon"
        [matTooltip]="nonPublishableTooltip"
        [ngClass]="customPublishIconClass"
        svgIcon="icon-no-publish"
      ></mat-icon>
      <ng-content></ng-content>
    </div>
    <div *ngIf="showImage" class="image-container" [ngClass]="color" [ngStyle]="backgroundImage$ | async"></div>
  `,
  styleUrls: ['./bg-image.component.scss'],
  standalone: false,
})
export class BgImageComponent implements OnChanges {
  @Input() defaultImagePath: string = defaultImagePlaceholder;
  @Input() imageSrc: string;
  @Input() publishable = true;
  @Input() nonPublishableTooltip: string;
  @Input() color: ImageContainerBGColor = 'default';
  @Input() customPublishIconClass = '';
  @Input() showImage: boolean = true;
  @Input() set secureImage(imageObj: SecureImage) {
    if (imageObj != null) {
      this.imageSrc = typeof imageObj.imageSrc === 'string' ? (imageObj.imageSrc as string) : null;
      this.publishable = imageObj.publishable;
      this.isPDF = imageObj.isPDF;
    }
  }
  isPDF: boolean;

  imageSrc$ = new BehaviorSubject<string>(null);
  backgroundImage$ = this.imageSrc$.pipe(
    startWith(this.defaultImagePath),
    filter(url => !!url),
    switchMap(url => this.loadImage(url).pipe(catchError(() => of(this.defaultImagePath)))),
    map(url => this.getBackgroundImageStyle(url))
  );

  constructor(private securedResourceResolver: MiqSecuredResourcesService) {}

  ngOnChanges() {
    this.imageSrc$.next(this.imageSrc);
  }

  loadImage(imageSrc) {
    if (imageSrc === this.defaultImagePath) {
      return of(imageSrc);
    }
    return this.securedResourceResolver.loadImage(imageSrc);
  }

  public getBackgroundImageStyle(imageSrc) {
    if (imageSrc === this.defaultImagePath) {
      // for svg coloring
      return {
        mask: `url(${imageSrc})`,
        '-webkit-mask': `url(${imageSrc})`,
        '-webkit-mask-size': 'cover',
      };
    }
    return {
      'background-image': `url(${imageSrc})`,
      'background-position': 'center center',
      'background-size': 'contain',
      'background-repeat': 'no-repeat',
    };
  }
}
